import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { bindSignInQuery, signInPresentationInput } from "../../../shared/src/site-auth-query";
import type { SignInPage } from "../server/auth.server";
import type { signInDeps } from "./auth-route";
import { searchParams, type RawSearch } from "./search";

export function publicSignInInput<T extends { signUp: boolean }>(input: T) {
  return typeof document !== "undefined" &&
    document.documentElement.hasAttribute("data-publication")
    ? signInPresentationInput(input.signUp)
    : input;
}

/** URL hints do not invalidate the prepared loader during hydration or method changes. */
export function publicSignInDeps(input: ReturnType<typeof signInDeps>) {
  return typeof document !== "undefined" &&
    document.documentElement.hasAttribute("data-publication")
    ? { returnTo: null, method: null, sent: null, error: null, reset: false }
    : input;
}

/** First render matches the prepared document; changing method/feedback needs no server render. */
export function useSignInQuery<T extends SignInPage>(initial: T) {
  const search = useRouterState({ select: (state) => state.location.searchStr });
  const [view, setView] = useState(initial);
  useEffect(() => {
    setView(bindSignInQuery(initial, new URLSearchParams(search)));
  }, [initial, search]);
  return view;
}

export function useAuthSearch(initial: RawSearch, prepared: boolean) {
  const search = useRouterState({ select: (state) => state.location.searchStr });
  const [value, setValue] = useState({ search: prepared ? {} : initial, ready: !prepared });
  useEffect(() => {
    setValue({ search: Object.fromEntries(new URLSearchParams(search)), ready: true });
  }, [search]);
  return { query: searchParams(value.search), ready: value.ready };
}
